import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useSEO } from '@/hooks/useSEO';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { validatePassword, logSecurityEvent } from '@/lib/validation';
import { useAuth } from '@/hooks/useAuth';

const ResetPassword = () => {
  // SEO Configuration
  useSEO({
    title: "Set New Password - ChessHatch Account Security",
    description: "Create a new secure password for your ChessHatch account. Complete your password reset process to regain access to chess tutorials, games, and community features.",
    keywords: "chess new password, account security, password change, chess account protection"
  });

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [isValidSession, setIsValidSession] = useState(false);
  const [checkingSession, setCheckingSession] = useState(true);
  const [showLocalhostWarning, setShowLocalhostWarning] = useState(false);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();

  useEffect(() => {
    const checkPasswordResetSession = async () => {
      try {
        // Detect if user clicked localhost link but is on production
        const isLocalhost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
        const referrer = document.referrer;
        
        if (!isLocalhost && referrer.includes('localhost')) {
          setShowLocalhostWarning(true);
          setCheckingSession(false);
          return;
        }

        // Parse tokens from both URL hash (Supabase default) and search params (fallback)
        const hashParams = new URLSearchParams(window.location.hash.replace(/^#/, ''));
        const accessToken = hashParams.get('access_token') || searchParams.get('access_token');
        const refreshToken = hashParams.get('refresh_token') || searchParams.get('refresh_token');
        const type = hashParams.get('type') || searchParams.get('type');

        if (type === 'recovery' && accessToken && refreshToken) {
          // Set the session from the URL tokens
          const { error } = await supabase.auth.setSession({
            access_token: accessToken,
            refresh_token: refreshToken,
          });

          if (error) {
            // Check if error is due to localhost redirect
            if (error.message.includes('localhost') || error.message.includes('127.0.0.1')) {
              setShowLocalhostWarning(true);
              setCheckingSession(false);
              return;
            }
            
            toast.error('Invalid or expired reset link');
            navigate('/forgot-password');
            return;
          }

          setIsValidSession(true);
        } else if (user) {
          // User is logged in, might be coming from a direct link
          setIsValidSession(true);
        } else {
          // No valid session for password reset
          toast.error('Invalid password reset session');
          navigate('/forgot-password');
          return;
        }
      } catch (error: any) {
        console.error('Session check error:', error);
        
        // Check if error is due to localhost redirect
        if (error?.message?.includes('localhost') || error?.message?.includes('127.0.0.1')) {
          setShowLocalhostWarning(true);
          setCheckingSession(false);
          return;
        }
        
        toast.error('Session validation failed');
        navigate('/forgot-password');
      } finally {
        setCheckingSession(false);
      }
    };

    checkPasswordResetSession();
  }, [searchParams, navigate, user]);

  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Password validation
    const passwordValidation = validatePassword(password);
    if (!passwordValidation.isValid) {
      toast.error(passwordValidation.error);
      return;
    }
    
    // Confirm password match
    if (password !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    setLoading(true);
    
    try {
      const { error } = await supabase.auth.updateUser({
        password: password
      });
      
      if (error) {
        toast.error(error.message);
        logSecurityEvent('password_reset_failed', user?.id, { error: error.message });
      } else {
        toast.success('Password updated successfully!');
        logSecurityEvent('password_reset_completed', user?.id);
        
        // Clear the URL hash and redirect to home
        window.history.replaceState({}, '', window.location.pathname);
        navigate('/', { replace: true });
      }
    } catch (error: any) {
      toast.error('Failed to update password');
      logSecurityEvent('password_reset_error', user?.id, { error: error.message });
    } finally {
      setLoading(false);
    }
  };

  if (checkingSession) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-muted via-background to-secondary flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardContent className="pt-6">
            <div className="text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
              <p className="text-muted-foreground">Validating reset session...</p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (showLocalhostWarning) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-muted via-background to-secondary flex items-center justify-center p-4">
        <div className="w-full max-w-md">
          <Card className="border-amber-500/50">
            <CardHeader>
              <CardTitle className="text-amber-600 dark:text-amber-400">⚠️ Invalid Reset Link</CardTitle>
              <CardDescription>
                This password reset link is configured for localhost (local development).
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="p-4 bg-muted rounded-lg space-y-3">
                <p className="text-sm font-medium">Why did this happen?</p>
                <p className="text-sm text-muted-foreground">
                  This email was sent when the app was configured for local development. The reset link points to localhost, which doesn't work in your browser.
                </p>
              </div>
              
              <div className="p-4 bg-primary/10 rounded-lg space-y-3">
                <p className="text-sm font-medium">Quick Solution:</p>
                <p className="text-sm text-muted-foreground">
                  Request a new password reset email. The new email will have a working link:
                </p>
              </div>

              <Button 
                className="w-full"
                onClick={() => navigate('/forgot-password')}
              >
                Request New Reset Email
              </Button>

              <div className="text-center pt-2">
                <Button 
                  variant="ghost" 
                  onClick={() => navigate('/')}
                  className="text-muted-foreground hover:text-foreground"
                >
                  ← Back to Home
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  if (!isValidSession) {
    return null; // Will redirect in useEffect
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-muted via-background to-secondary flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-foreground mb-2">Set New Password</h1>
          <p className="text-muted-foreground">Choose a strong password for your account</p>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>Reset Your Password</CardTitle>
            <CardDescription>
              Enter your new password below. Make sure it's strong and secure!
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleResetPassword} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="password">New Password</Label>
                <Input
                  id="password"
                  type="password"
                  placeholder="Enter your new password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  minLength={6}
                />
                <p className="text-xs text-muted-foreground">
                  Must be at least 6 characters with uppercase and lowercase letters
                </p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="confirmPassword">Confirm New Password</Label>
                <Input
                  id="confirmPassword"
                  type="password"
                  placeholder="Confirm your new password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                  minLength={6}
                />
              </div>
              <Button type="submit" className="w-full" disabled={loading}>
                {loading ? 'Updating Password...' : 'Update Password'}
              </Button>
            </form>
          </CardContent>
        </Card>

        <div className="text-center mt-4">
          <Button 
            variant="ghost" 
            onClick={() => navigate('/')}
            className="text-muted-foreground hover:text-foreground"
          >
            ← Back to Home
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ResetPassword;